// =============================================================================
// ui/tabs/ResultsCurve.tsx — the Results board's cumulative points curve, with
// the underwater (drawdown-from-peak) strip beneath it. #58.
// -----------------------------------------------------------------------------
// Two panels, one SVG, one shared x-axis — that pairing is the whole point. A
// cumulative curve alone flatters a strategy: a book that ends +400 pts looks
// identical whether it climbed steadily or gave back 180 on the way. The strip
// underneath is the only place on this board that shows the giving-back, and it
// has to sit directly under the curve on the same x so the trader can read the
// two together.
//
// Geometry (the viewBox, the band offsets, the padding) is beta's, unchanged —
// backtest-dashboard.html:561-592.
//
// ── 🚨 THE STRETCH, AND WHY NOTHING WITH A SHAPE LIVES IN THIS SVG ──
//
// The SVG is `preserveAspectRatio="none"`, so its x and y units scale
// INDEPENDENTLY to fill the card. Beta does the same AND puts its axis labels
// inside as `<text>`, so on a wide card beta's labels are horizontally smeared —
// there is no `vector-effect` for glyphs. `ui/charts/EquityCurve.tsx` ruled that
// out already; its header is the reference. This file follows it and goes one
// step further:
//
//   EVERY GLYPH AND EVERY ROUND MARKER IS HTML POSITIONED OVER THE CHART.
//   The SVG holds only lines, paths and fills.
//
// Beta draws its end-of-curve dot and its drawdown-trough dot as `<circle>`, so
// both paint as ellipses whose eccentricity changes with the card width. Moving
// them to HTML is a deliberate divergence from beta toward the geometry beta
// intended; the 9-unit halo behind the end dot is where the ovalling is most
// obvious.
//
// ⚠ THE OVERLAY POSITIONS ARE PERCENTAGES OF THE SAME BOX THE SVG FILLS. That
// only holds if `.bd-cv-plot` is `position: relative` and `.bd-cv-svg` covers it
// exactly (`position:absolute; inset:0; width:100%; height:100%`). If the SVG is
// ever inset from its wrapper, every label and both markers drift and nothing
// throws.
//
// ── ⚠ NO ARITHMETIC ON THE VALUES ──
//
// Every number this panel PRINTS arrives on props. What is computed here is
// screen geometry only — where a value lands in the viewBox — which is the same
// scaling beta does inside its own render function.
// =============================================================================

import { useId } from 'react';
import { num } from '../format';

// ── beta's box, verbatim (backtest-dashboard.html:552-553) ──────────────────
const VB_W = 1000;
const VB_H = 250;
const PAD_L = 6;
const PAD_R = 6;
/** The equity band: the curve lives between these two y's. */
const EQ_TOP = 16;
const EQ_BOT = 168;
/** The underwater strip, below the band with a gap for its label. */
const UW_TOP = 198;
const UW_BOT = 240;

/** viewBox x → a percentage of the wrapper, for the HTML overlays. */
const pctX = (x: number): string => `${((x / VB_W) * 100).toFixed(3)}%`;
/** viewBox y → a percentage of the wrapper. */
const pctY = (y: number): string => `${((y / VB_H) * 100).toFixed(3)}%`;

export interface ResultsCurveProps {
  /** The cumulative net-points curve. Starts at 0, so index 0 is "before the first sample". */
  series: readonly number[];
  /** Distance below the running peak at each point of `series`. Same length, every value ≤ 0. */
  underwater: readonly number[];
  /** The deepest of those, ≤ 0. Printed as-is — never recomputed here. */
  maxDrawdown: number;
  /**
   * What one bucket IS — "week" or "month".
   *
   * 🚨 A PROP, NOT A LITERAL. The first version hardcoded "weeks", so switching
   * the board to Monthly relabelled twelve months as twelve weeks and left the
   * sentence reading as a fact. Caught by rendering it, not by reading it.
   */
  bucketNoun: 'week' | 'month';
  samples: number;
  /** Gridline positions. `index` indexes `series`; `label` is already formatted, e.g. "wk Mar 29". */
  buckets: readonly { label: string; index: number }[];
}

export function ResultsCurve({
  series, underwater, maxDrawdown, samples, buckets, bucketNoun,
}: ResultsCurveProps) {
  // ⚠ A GENERATED GRADIENT ID, NOT A LITERAL. Two of these on one page — the
  // board and a pattern drill-down over it — would otherwise share `#bd-cv-fill`
  // and the second mount would silently restyle the first. Same trap, same fix
  // as ui/charts/EquityCurve.tsx.
  const gid = useId().replace(/:/g, '');

  // ⚠ ONE POINT IS NOT A CURVE. `series` opens at 0 before any sample, so
  // length 1 means an empty book and length 0 means the selector had nothing at
  // all. Beta never renders this state — its board only ever runs with data —
  // so the string below is this client's, not beta's.
  //
  // ⚠ The meta line is deliberately withheld here too: "max drawdown 0.0 pts"
  // under a blank chart reads as a measured result (you never went underwater)
  // when it actually means there is nothing to measure.
  if (series.length < 2) {
    return (
      <section className="bd-cv-card" data-testid="results-curve">
        <div className="bd-cv-head">
          <span className="bd-cv-eyebrow">Cumulative</span>
          <h3 className="bd-cv-title">Net points banked over the test window</h3>
        </div>
        <p className="bd-cv-empty">
          Not enough samples to draw a curve yet.
        </p>
      </section>
    );
  }

  const lastIdx = series.length - 1;

  // ── the equity band's scale ────────────────────────────────────────────────
  // ⚠ `Math.max(1, …)` and `Math.min(0, …)` are beta's, and they are not
  // defensive rounding: they PIN ZERO INSIDE THE BAND. Without the min(0) an
  // all-winning book would have its baseline float somewhere above the bottom
  // and the shaded area would stop meaning "profit above break-even".
  const scaleTop = Math.max(1, ...series);
  const scaleBot = Math.min(0, ...series);
  const span = (scaleTop - scaleBot) || 1;

  const xAt = (i: number): number => PAD_L + (i / lastIdx) * (VB_W - PAD_L - PAD_R);
  const yEq = (v: number): number => EQ_TOP + (1 - (v - scaleBot) / span) * (EQ_BOT - EQ_TOP);
  const zeroY = yEq(0);

  const line = series
    .map((v, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)} ${yEq(v).toFixed(1)}`)
    .join(' ');
  // Closed down to the ZERO line, not to the bottom of the band — so the fill
  // under a curve that dips negative is on the far side of break-even from the
  // fill above it, rather than one solid block.
  const area = `${line} L${xAt(lastIdx).toFixed(1)} ${zeroY.toFixed(1)}`
    + ` L${xAt(0).toFixed(1)} ${zeroY.toFixed(1)} Z`;

  // ── the underwater strip's scale ──────────────────────────────────────────
  // ⚠ THE DIVISOR IS CLAMPED, THE PRINTED NUMBER IS NOT. `maxDrawdown` is 0 for
  // a book that never gave anything back, and 0 as a divisor turns every strip
  // coordinate into Infinity — an invisible, silently broken panel. Beta clamps
  // the same way (`Math.min(-0.1, …)`); the difference is that beta then PRINTS
  // its clamped value, so beta's board reports "max drawdown −0.1 pts" for a
  // book that never had one. See the trough marker below.
  const ddDenom = Math.min(-0.1, maxDrawdown);
  const yUw = (dd: number): number => UW_TOP + (dd / ddDenom) * (UW_BOT - UW_TOP);

  // ⚠ `underwater` is the same length as `series` by construction (one selector
  // builds both from one array). Its own last index is used anyway, so a
  // mismatch draws a short strip instead of a path running off the right edge.
  const uwLast = underwater.length - 1;
  const uwLine = underwater
    .map((dd, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)} ${yUw(dd).toFixed(1)}`)
    .join(' ');
  const uwArea = `${uwLine} L${xAt(uwLast).toFixed(1)} ${UW_TOP}`
    + ` L${xAt(0).toFixed(1)} ${UW_TOP} Z`;

  // The deepest point, for the marker. A scan for the lowest value already in
  // the array — not a recomputation of the depth, which is the prop.
  let troughIdx = 0;
  for (let i = 1; i < underwater.length; i += 1) {
    if ((underwater[i] ?? 0) < (underwater[troughIdx] ?? 0)) troughIdx = i;
  }

  const endValue = series[lastIdx] ?? 0;
  const endX = xAt(lastIdx);
  const endY = yEq(endValue);

  return (
    <section className="bd-cv-card" data-testid="results-curve">
      <div className="bd-cv-head">
        {/* ⚠ Sentence case in the DOM, uppercased by `.bd-cv-eyebrow` — exactly
            as beta does it (its `.eyebrow` carries `text-transform:uppercase`).
            Shouting in the markup would be read out letter-by-letter by some
            screen readers. The same split applies to the strip label below. */}
        <span className="bd-cv-eyebrow">Cumulative</span>
        <h3 className="bd-cv-title">Net points banked over the test window</h3>
        <span className="bd-cv-note">
          {samples} samples · {buckets.length} {bucketNoun}{buckets.length === 1 ? '' : 's'} · max drawdown{' '}
          <b className="bd-cv-neg">{num(maxDrawdown, 1)} pts</b>
        </span>
      </div>

      <div className="bd-cv-wrap">
        <div className="bd-cv-plot" style={{ height: VB_H }}>
          <svg
            className="bd-cv-svg"
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            // ⚠ See the header. Every other decision in this file serves this one.
            preserveAspectRatio="none"
            role="img"
            aria-label={
              `Cumulative net points across ${samples} samples, ending `
              + `${num(endValue, 1)} points, deepest drawdown ${num(maxDrawdown, 1)} points`
            }
          >
            <defs>
              <linearGradient id={`bd-cv-fill-${gid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="var(--green)" stopOpacity="0.28" />
                <stop offset="1" stopColor="var(--green)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Break-even. Drawn under everything so the curve crosses it. */}
            <line
              x1={PAD_L} y1={zeroY.toFixed(1)} x2={VB_W - PAD_R} y2={zeroY.toFixed(1)}
              stroke="var(--border-2)" vectorEffect="non-scaling-stroke"
            />

            {/* ⚠ Gridlines span the EQUITY BAND ONLY, as beta's do — carrying
                them through the strip would fence the drawdown shape into
                boxes and make a continuous give-back look like several. */}
            {buckets.map((b) => (
              <line
                key={`${b.index}-${b.label}`}
                x1={xAt(b.index).toFixed(1)} y1={EQ_TOP}
                x2={xAt(b.index).toFixed(1)} y2={EQ_BOT}
                stroke="var(--border)" strokeDasharray="2 4"
                vectorEffect="non-scaling-stroke"
              />
            ))}

            <path d={area} fill={`url(#bd-cv-fill-${gid})`} />
            {/* ⚠ `non-scaling-stroke` on every stroked element, not just this
                one: the SVG stretches, so without it a 2.4-unit line paints
                thick horizontally and hairline vertically, and the gridlines'
                dash pattern stretches with it. Beta omits it and shows both. */}
            <path
              d={line} fill="none" stroke="var(--green)" strokeWidth={2.4}
              strokeLinejoin="round" vectorEffect="non-scaling-stroke"
            />

            {/* The strip's own top edge — the "waterline". Depth is measured
                down from here, so it is a real axis, not a divider. */}
            <line
              x1={PAD_L} y1={UW_TOP} x2={VB_W - PAD_R} y2={UW_TOP}
              stroke="var(--border)" vectorEffect="non-scaling-stroke"
            />
            {uwLast > 0 ? (
              <>
                <path d={uwArea} fill="var(--red)" opacity={0.16} />
                <path
                  d={uwLine} fill="none" stroke="var(--red)" strokeWidth={1.4}
                  opacity={0.85} vectorEffect="non-scaling-stroke"
                />
              </>
            ) : null}
          </svg>

          {/* ── HTML OVER THE CHART, NOT SVG <text>/<circle> ─────────────────
              Everything below is positioned as a percentage of the same box the
              SVG fills, so it tracks the identical geometry without being
              stretched by it. See the header. */}

          {/* ⚠ Expected to sit ABOVE the waterline — `.bd-cv-striplab` needs
              `transform: translateY(-100%)`, matching beta's baseline at
              `uTop - 5`. And `text-transform: uppercase`, as beta's `.sidelab`
              carries, so this reads "UNDERWATER · DRAWDOWN FROM PEAK (PTS)" on
              screen while staying sentence case in the DOM. */}
          <span className="bd-cv-striplab" style={{ left: pctX(PAD_L), top: pctY(UW_TOP) }}>
            Underwater · drawdown from peak (pts)
          </span>

          {/* The end of the curve — where the book actually finished. The halo
              is the reason this is HTML: as an SVG circle it paints as an
              ellipse that changes shape with the card width. */}
          <span
            className="bd-cv-end"
            style={{ left: pctX(endX), top: pctY(endY) }}
            aria-hidden="true"
          />

          {/* ── 🚨 NO TROUGH MARKER WHEN THERE IS NO DRAWDOWN ──
              Beta always draws one, because it marks its CLAMPED divisor: on a
              book that never went underwater beta pins a dot at the very bottom
              of the strip and labels it "−0.1 max", inventing a give-back that
              never happened and putting it at maximum visual depth. That is the
              one thing on this panel that could mislead a trader about their
              own results, so the marker is omitted instead. */}
          {maxDrawdown < 0 ? (
            <>
              <span
                className="bd-cv-ddmark"
                style={{ left: pctX(xAt(troughIdx)), top: pctY(yUw(underwater[troughIdx] ?? 0)) }}
                aria-hidden="true"
              />
              {/* ⚠ The PROP, not the value at the trough. Both come from the
                  same selector over the same series and agree; printing the
                  prop guarantees this label and the meta line above can never
                  disagree about the same number on one screen. */}
              <span
                className="bd-cv-ddval"
                style={{
                  left: pctX(xAt(troughIdx)),
                  top: pctY(yUw(underwater[troughIdx] ?? 0) + 13),
                }}
              >
                {num(maxDrawdown, 1)} max
              </span>
            </>
          ) : null}

          {/* ⚠ Beta right-aligns these to their gridline (`text-anchor="end"`),
              because a bucket label names the slice ENDING at that line. The
              class has to reproduce it with `transform: translateX(-100%)`;
              centring them would attribute each week to the wrong gridline. */}
          {buckets.map((b) => (
            <span
              key={`${b.index}-${b.label}`}
              className="bd-cv-xlab"
              style={{ left: pctX(xAt(b.index)) }}
            >
              {b.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
